import { Directive, input, output } from '@angular/core';

/** Desplazamiento horizontal mínimo (px) para leer el gesto como swipe y no como tap. */
const UMBRAL_PX = 40;

/**
 * Índice siguiente de un carrusel, con wrap en los extremos: de la última foto
 * se vuelve a la primera y viceversa.
 */
export function indiceConWrap(actual: number, delta: number, total: number): number {
  if (total <= 1) return 0;
  return (actual + delta + total) % total;
}

/**
 * Swipe horizontal táctil para los carruseles (RP-11). Emite `(swipe)` con -1
 * (anterior) o +1 (siguiente) cuando el gesto fue deliberadamente horizontal.
 *
 * Portada de `swipe-horizontal.directive.ts` del backoffice, con el mismo
 * umbral y el mismo criterio para descartar un gesto vertical: el carrusel de
 * la tarjeta es el mismo en los dos frontends y no tiene sentido que el gesto
 * se sienta distinto según la aplicación. Se adaptó el selector a la convención
 * de acá (kebab-case, como `svg[app-icono]`).
 *
 * El scroll vertical de la página no se toca: el gesto se descarta si fue más
 * vertical que horizontal, y el `touch-action` de cada componente deja pasar el
 * scroll nativo. Sin él, arrastrar en diagonal sobre la foto lo traba. Lo que no
 * puede llevar es `pan-x`: ahí el arrastre horizontal se lo queda el navegador y
 * el gesto no llega. `propiedad-card` usa `pan-y pinch-zoom`.
 *
 * En un dispositivo sin touch no engancha nada: los eventos no llegan nunca, y
 * las flechas siguen siendo la única forma de pasar de foto.
 *
 * ```html
 * <div app-swipe-horizontal [swipeDeshabilitado]="!tieneVarias()" (swipe)="mover($event)">
 * ```
 */
@Directive({
  selector: '[app-swipe-horizontal]',
  host: {
    '(touchstart)': 'alIniciar($event)',
    '(touchend)': 'alTerminar($event)',
  },
})
export class SwipeHorizontal {
  /** Con una sola foto no hay nada que swipear: apaga la detección entera. */
  readonly swipeDeshabilitado = input(false);

  /** -1 = anterior, +1 = siguiente. */
  readonly swipe = output<-1 | 1>();

  private inicioX: number | null = null;
  private inicioY: number | null = null;

  alIniciar(event: TouchEvent): void {
    if (this.swipeDeshabilitado()) return;
    // changedTouches en lugar de touches: en el touchstart son equivalentes para
    // un dedo, pero changedTouches trae SOLO el dedo que acaba de tocar, así que
    // no se mezcla con un dedo previo si el gesto arranca multitouch.
    const touch = event.changedTouches[0];
    this.inicioX = touch?.clientX ?? null;
    this.inicioY = touch?.clientY ?? null;
  }

  alTerminar(event: TouchEvent): void {
    const inicioX = this.inicioX;
    const inicioY = this.inicioY;
    this.inicioX = null;
    this.inicioY = null;
    if (inicioX == null || inicioY == null) return;

    const touch = event.changedTouches[0];
    if (!touch) return;
    const deltaX = touch.clientX - inicioX;
    const deltaY = touch.clientY - inicioY;

    // Tap (no llegó al umbral) o gesto mayormente vertical (scroll de la
    // página): no es swipe. El `<=` descarta también la diagonal exacta.
    if (Math.abs(deltaX) < UMBRAL_PX || Math.abs(deltaX) <= Math.abs(deltaY)) return;

    // Cancela el click sintético posterior al touchend: sin esto, el swipe sobre
    // la foto además dispararía el (click) que navega a la ficha.
    event.preventDefault();
    this.swipe.emit(deltaX < 0 ? 1 : -1);
  }
}
